import { GOOGLE_MAPS_API_KEY } from "./googleMapsConfig";
import React, {
  useMemo,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import {
  GoogleMap,
  Marker,
  useJsApiLoader,
  Autocomplete,
  DirectionsRenderer,
  Polyline,
  Polygon,
  Circle,
  OverlayView,
} from "@react-google-maps/api";
import { Button, Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import styled from "styled-components";

const LIBRARIES = ["places"];
const MapContainer = styled.div`
  width: 100%;
  height: ${(props) => (props.mapSize === "large" ? "1000px" : "300px")};
  position: relative;

  .search-container {
    position: absolute;
    top: 5px;
    left: 60px;
    right: 10px;
    z-index: 10;
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    padding: 8px;
  }

  .map-controls {
    position: absolute;
    top: 50px;
    right: 10px;
    z-index: 10;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .satellite-toggle {
    margin-top: 50px;
    background: white;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    padding: 8px 12px;
    cursor: pointer;
    font-size: 16px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    &:hover {
      background: #f5f5f5;
    }
  }
`;

const defaultCenter = {
  lat: -1.286389,
  lng: 36.817223,
};

const GoogleMapComponent = ({
  rGeoJSON,
  mapSize = "medium",
  routeData = [],
  resetTrigger,
  showSearch = false,
  className,
  fencesList = [],
  selectedFences = [],
  mode = "view",
  // drawing props (for GeoFence screen)
  fenceData,
  drawingMode,
  isEditing,
  onPolygonDraw,
  initialPolygon,
  onPathChange,
  // circular fence props
  fenceStyle = "1", // "2" = Circle, "1" = Polygon
  radius = 3, // default radius in km
  onCircleDraw,
}) => {
  const [map, setMap] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [fencePolygons, setFencePolygons] = useState([]);
  const [directions, setDirections] = useState(null);
  const [isSatellite, setIsSatellite] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapZoom, setMapZoom] = useState(10);
  const [searchLocation, setSearchLocation] = useState(null);
  const autocompleteRef = useRef(null);
  const mapRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });

  // Manage a user-drawn/edited polygon (for GeoFence create/edit)
  const drawnPolygonRef = useRef(null);
  const drawnCircleRef = useRef(null);
  const [circleCenter, setCircleCenter] = useState(null);
  const lastCircleCenterRef = useRef(null);
  const lastCircleRadiusRef = useRef(null);
  const [customDrawingActive, setCustomDrawingActive] = useState(false);
  const [inProgressPoints, setInProgressPoints] = useState([]);
  const inProgressPointsRef = useRef([]);
  const clickTimerRef = useRef(null);
  const [polygonDrawn, setPolygonDrawn] = useState(false);

  const extractPath = useCallback((polygon) => {
    if (!polygon) return [];
    const path = polygon.getPath();
    const points = [];
    for (let i = 0; i < path.getLength(); i++) {
      const ll = path.getAt(i);
      points.push({ lat: ll.lat(), lng: ll.lng() });
    }
    return points;
  }, []);

  const wirePolygonListeners = useCallback(
    (polygon) => {
      if (!polygon) return;
      const notify = () => {
        const pts = extractPath(polygon);
        onPathChange && onPathChange(pts);
      };
      const path = polygon.getPath();
      path.addListener("set_at", notify);
      path.addListener("insert_at", notify);
      path.addListener("remove_at", notify);
    },
    [extractPath, onPathChange],
  );

  // ROUTE MANAGEMENT

  // Process route data to create markers and directions
  const processRouteData = useCallback(() => {
    if (!routeData) return;

    const newMarkers = [];
    const newFencePolygons = [];
    let waypoints = [];

    // Handle different data structures
    if (Array.isArray(routeData)) {
      // Handle fence data (for add mode)
      routeData.forEach((fence, index) => {
        if (fence.FCenterLat && fence.FCenterLon) {
          const marker = {
            id: fence.FGUID || index,
            position: {
              lat: parseFloat(fence.FCenterLat),
              lng: parseFloat(fence.FCenterLon),
            },
            title: fence.FName || `Fence ${index + 1}`,
            type: "fence",
            data: fence,
          };
          newMarkers.push(marker);

          // Process fence polygon if FPath exists
          if (fence.FPath) {
            try {
              const path = JSON.parse(fence.FPath);
              // Filter out invalid points
              const validPath = path.filter(
                (p) =>
                  typeof p.lat === "number" &&
                  typeof p.lng === "number" &&
                  !isNaN(p.lat) &&
                  !isNaN(p.lng),
              );

              if (validPath.length >= 3) {
                const polygonPath = validPath.map((p) => ({
                  lat: parseFloat(p.lat),
                  lng: parseFloat(p.lng),
                }));

                const fencePolygon = {
                  id: fence.FGUID || index,
                  path: polygonPath,
                  fence: fence,
                };
                newFencePolygons.push(fencePolygon);
              }
            } catch (error) {
              console.error("Error parsing fence path:", error);
            }
          }

          if (index > 0 && index < routeData.length - 1) {
            waypoints.push({
              location: marker.position,
              stopover: true,
            });
          }
        }
      });
    } else if (routeData.FRoadPoints) {
      // Handle route data (for edit mode) - show only key fence markers, not every route point
      try {
        const points = JSON.parse(routeData.FRoadPoints);
        if (points.length > 0) {
          // Show only start and end markers, let processEditModeFences handle the actual fence markers
          const startMarker = {
            id: "start_point",
            position: {
              lat: parseFloat(points[0].lat),
              lng: parseFloat(points[0].lng),
            },
            title: routeData.FStartFenceName,
            type: "start",
            data: points[0],
          };
          // newMarkers.push(startMarker);

          if (points.length > 1) {
            const endMarker = {
              id: "end_point",
              position: {
                lat: parseFloat(points[points.length - 1].lat),
                lng: parseFloat(points[points.length - 1].lng),
              },
              title: routeData.FEndFenceName,
              type: "end",
              data: points[points.length - 1],
            };
            // newMarkers.push(endMarker);
          }
        }
      } catch (error) {
        console.error("Error parsing route points:", error);
      }
    }

    setMarkers(newMarkers);
    setFencePolygons(newFencePolygons);

    // Set map center to first marker if available
    if (newMarkers.length > 0) {
      setMapCenter(newMarkers[0].position);
      setMapZoom(16);
    }
  }, [routeData]);

  // Process fences for edit mode - show fences that are part of the route
  const processEditModeFences = useCallback(() => {
    if (mode !== "edit" || !fencesList || !selectedFences) {
      return;
    }

    const newFencePolygons = [];
    const newFenceMarkers = [];

    // Show fences that are selected/part of the route
    selectedFences.forEach((fenceId) => {
      const fence = fencesList.find((f) => String(f.FGUID) === String(fenceId));
      if (fence) {
        // Add fence center marker
        if (fence.FCenterLat && fence.FCenterLon) {
          const fenceMarker = {
            id: fence.FGUID,
            position: {
              lat: parseFloat(fence.FCenterLat),
              lng: parseFloat(fence.FCenterLon),
            },
            title: fence.FName || "Fence",
            type: "fence",
            data: fence,
          };
          newFenceMarkers.push(fenceMarker);
        }

        // Add fence polygon if FPath exists
        if (fence.FPath) {
          try {
            const path = JSON.parse(fence.FPath);
            // Filter out invalid points
            const validPath = path.filter(
              (p) =>
                typeof p.lat === "number" &&
                typeof p.lng === "number" &&
                !isNaN(p.lat) &&
                !isNaN(p.lng),
            );

            if (validPath.length >= 3) {
              const polygonPath = validPath.map((p) => ({
                lat: parseFloat(p.lat),
                lng: parseFloat(p.lng),
              }));

              const fencePolygon = {
                id: fence.FGUID,
                path: polygonPath,
                fence: fence,
              };
              newFencePolygons.push(fencePolygon);
            }
          } catch (error) {
            console.error("Error parsing fence path:", error);
          }
        }
      } else {
      }
    });

    setFencePolygons((prev) => [...prev, ...newFencePolygons]);
    setMarkers((prev) => [...prev, ...newFenceMarkers]);
  }, [mode, fencesList, selectedFences]);

  // Process GeoJSON data for route visualization
  const processGeoJSON = useCallback(() => {
    if (!rGeoJSON || !map) return;

    if (rGeoJSON.type === "LineString" && rGeoJSON.coordinates) {
      const path = rGeoJSON.coordinates.map((coord) => ({
        lat: coord[1],
        lng: coord[0],
      }));

      // Create a polyline for the route
      const polyline = new window.google.maps.Polyline({
        path: path,
        geodesic: true,
        strokeColor: "#06CE06",
        strokeOpacity: 1.0,
        strokeWeight: 4,
      });

      polyline.setMap(map);
    }
  }, [rGeoJSON, map]);

  // Build a polyline directly from routeData.FRoadPoints when available
  const routePoints = (() => {
    try {
      if (routeData && typeof routeData === "object" && routeData.FRoadPoints) {
        const pts = JSON.parse(routeData.FRoadPoints || "[]");
        if (Array.isArray(pts) && pts.length > 1) {
          return pts
            .filter(
              (p) =>
                typeof p.lat !== "undefined" &&
                typeof p.lng !== "undefined" &&
                !isNaN(Number(p.lat)) &&
                !isNaN(Number(p.lng)),
            )
            .map((p) => ({ lat: Number(p.lat), lng: Number(p.lng) }));
        }
      }
    } catch (e) {
      // ignore
    }
    return null;
  })();

  // Handle search functionality
  const onPlaceChanged = useCallback(() => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place.geometry) {
        const location = place.geometry.location;
        const latLng = {
          lat: location.lat(),
          lng: location.lng(),
        };

        setMapCenter(latLng);
        setMapZoom(17);
        setSearchLocation(latLng);

        if (map) {
          map.panTo(latLng);
        }

        // If in circle mode and editing, draw circle at searched location
        if (isEditing && fenceStyle === "2") {
          drawCircleAtLocation(latLng, radius);
        }
      }
    }
  }, [map, isEditing, fenceStyle, radius]);

  // Handle satellite view toggle
  const toggleSatellite = useCallback(() => {
    if (map) {
      const currentMapType = map.getMapTypeId();
      const newMapType = currentMapType === "hybrid" ? "roadmap" : "hybrid";
      map.setMapTypeId(newMapType);
      setIsSatellite(newMapType === "hybrid");
    }
  }, [map]);

  // Handle marker click
  const onMarkerClick = useCallback(
    (marker) => {
      setSelectedLocation(marker);
      if (map) {
        map.panTo(marker.position);
      }
    },
    [map],
  );

  // Draw circle at specific location
  const drawCircleAtLocation = useCallback(
    (center, customRadius) => {
      if (!map || !isEditing || fenceStyle !== "2") return;
      const useRadius =
        typeof customRadius === "number" ? customRadius : radius;

      setCircleCenter(center);
      // Calculate area and notify parent
      const areaSqKm = Math.PI * useRadius * useRadius;
      onCircleDraw &&
        onCircleDraw({ center, radius: useRadius, area: areaSqKm });
      // If a circle instance exists, sync it as well
      if (drawnCircleRef.current) {
        drawnCircleRef.current.setCenter(center);
        drawnCircleRef.current.setRadius(radius * 1000);
      }
      lastCircleCenterRef.current = center;
      lastCircleRadiusRef.current = useRadius;
    },
    [map, isEditing, fenceStyle, radius, onCircleDraw],
  );

  const onMapClick = useCallback(
    (event) => {
      if (!isEditing || fenceStyle !== "2") return;

      const latLng = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      };

      drawCircleAtLocation(latLng, radius);
    },
    [isEditing, fenceStyle, drawCircleAtLocation],
  );

  const completePolygon = useCallback(() => {
    const pts = inProgressPointsRef.current;
    if (pts.length < 3) {
      window.alert("Please draw a polygon with at least 3 points to complete the shape.");
      return;
    }
    const polygon = new window.google.maps.Polygon({
      paths: pts,
      map: mapRef.current,
      fillColor: "#06CE06",
      fillOpacity: 0.3,
      strokeColor: "#06CE06",
      strokeOpacity: 1.0,
      strokeWeight: 2,
      editable: true,
      draggable: false,
    });
    if (drawnPolygonRef.current) {
      drawnPolygonRef.current.setMap(null);
    }
    drawnPolygonRef.current = polygon;
    wirePolygonListeners(polygon);
    onPolygonDraw && onPolygonDraw(pts);
    onPathChange && onPathChange(pts);
    setCustomDrawingActive(false);
    setPolygonDrawn(true);
    setInProgressPoints([]);
    inProgressPointsRef.current = [];
  }, [wirePolygonListeners, onPolygonDraw, onPathChange]);

  const handlePolygonPointAdd = useCallback((event) => {
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    const latLng = { lat: event.latLng.lat(), lng: event.latLng.lng() };
    clickTimerRef.current = setTimeout(() => {
      inProgressPointsRef.current = [...inProgressPointsRef.current, latLng];
      setInProgressPoints([...inProgressPointsRef.current]);
      clickTimerRef.current = null;
    }, 250);
  }, []);

  const handlePolygonDblClick = useCallback(() => {
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    completePolygon();
  }, [completePolygon]);

  useEffect(() => {
    if (isEditing && drawingMode === "polygon" && fenceStyle === "1") {
      const hasInitial = Array.isArray(initialPolygon) && initialPolygon.length > 2;
      setPolygonDrawn(hasInitial);
      setCustomDrawingActive(!hasInitial);
      setInProgressPoints([]);
      inProgressPointsRef.current = [];
    } else {
      setCustomDrawingActive(false);
    }
  }, [isEditing, drawingMode, fenceStyle, initialPolygon]);

  useEffect(() => {
    if (!routeData) return;
    // clear existing only once before processing
    setMarkers([]);
    setFencePolygons([]);
    processRouteData();
  }, [processRouteData]);

  useEffect(() => {
    processEditModeFences();
  }, [processEditModeFences]);

  useEffect(() => {
    processGeoJSON();
  }, [processGeoJSON]);

  // Fit map to available geometry (initial polygon, fence polygons, or markers)
  // Only auto-fit on initial load, not on every change to prevent zoom bouncing
  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;

    // Only auto-fit if we're not in view mode or if it's the initial load
    if (mode === "view" && mapRef.current.getZoom() !== 10) {
      return; // Don't auto-fit in view mode if user has already zoomed
    }

    // Prefer initial polygon if provided
    if (Array.isArray(initialPolygon) && initialPolygon.length > 2) {
      const bounds = new window.google.maps.LatLngBounds();
      initialPolygon.forEach((p) =>
        bounds.extend(
          new window.google.maps.LatLng(Number(p.lat), Number(p.lng)),
        ),
      );
      mapRef.current.fitBounds(bounds);
      return;
    }

    // Otherwise, fit to fence polygons if any
    if (Array.isArray(fencePolygons) && fencePolygons.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      fencePolygons.forEach((fp) => {
        fp.path.forEach((p) =>
          bounds.extend(
            new window.google.maps.LatLng(Number(p.lat), Number(p.lng)),
          ),
        );
      });
      mapRef.current.fitBounds(bounds);
      return;
    }

    // Otherwise, fit to markers (center/zoom for single, bounds for multiple)
    if (Array.isArray(markers) && markers.length > 0) {
      if (markers.length === 1) {
        mapRef.current.panTo(markers[0].position);
        mapRef.current.setZoom(16);
      } else {
        const bounds = new window.google.maps.LatLngBounds();
        markers.forEach((m) => bounds.extend(m.position));
        mapRef.current.fitBounds(bounds);
      }
    }
  }, [isLoaded, mapRef.current, initialPolygon, fencePolygons, markers, mode]);

  useEffect(() => {
    if (resetTrigger && map) {
      setMarkers([]);
      setFencePolygons([]);
      setDirections(null);
      setSelectedLocation(null);
      map.panTo(defaultCenter);
      map.setZoom(10);

      // Remove drawn polygon if exists
      if (drawnPolygonRef.current) {
        drawnPolygonRef.current.setMap(null);
        drawnPolygonRef.current = null;
      }
      // Remove drawn circle if exists
      if (drawnCircleRef.current) {
        drawnCircleRef.current.setMap(null);
        drawnCircleRef.current = null;
        setCircleCenter(null);
      }
      // Clear any in-progress drawing, then re-activate drawing if still in polygon mode
      const shouldDraw = isEditing && drawingMode === "polygon" && fenceStyle === "1";
      setCustomDrawingActive(shouldDraw);
      setPolygonDrawn(false);
      setInProgressPoints([]);
      inProgressPointsRef.current = [];
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
      }
    }
  }, [resetTrigger, map]);

  // Set crosshair cursor only while drawing (not after polygon is complete)
  useEffect(() => {
    if (!map) return;
    map.setOptions({
      draggableCursor:
        isEditing && fenceStyle === "1" && !polygonDrawn ? "crosshair" : undefined,
    });
  }, [map, isEditing, fenceStyle, polygonDrawn]);

  // Sync circle radius changes from props
  useEffect(() => {
    if (fenceStyle !== "2") return;
    // Avoid redundant updates
    if (lastCircleRadiusRef.current === radius) return;
    if (drawnCircleRef.current) {
      drawnCircleRef.current.setRadius(radius * 1000);
    }
    // Do not call onCircleDraw here to avoid update loops; it will be emitted from onRadiusChanged
    lastCircleRadiusRef.current = radius;
  }, [radius, fenceStyle]);

  // Initialize circle center from initialPolygon when in circle mode
  useEffect(() => {
    if (fenceStyle === "2" && initialPolygon && initialPolygon.length > 0) {
      const first = initialPolygon[0];
      if (
        first &&
        typeof first.lat !== "undefined" &&
        typeof first.lng !== "undefined"
      ) {
        setCircleCenter({ lat: Number(first.lat), lng: Number(first.lng) });
      }
    }
  }, [fenceStyle, initialPolygon]);
  const mapOptions = useMemo(() => {
    const zoomControlPosition =
      window.google && window.google.maps
        ? window.google.maps.ControlPosition.TOP_LEFT
        : undefined;
    const fullscreenControlPosition =
      window.google && window.google.maps
        ? window.google.maps.ControlPosition.BOTTOM_LEFT
        : undefined;

    const options = {
      disableDefaultUI: true,
      zoomControl: true,
      fullscreenControl: true,
      mapTypeId: isSatellite ? "hybrid" : "roadmap",
      gestureHandling: "greedy",
      clickableIcons: false,
    };

    if (zoomControlPosition) {
      options.zoomControlOptions = { position: zoomControlPosition };
    }
    if (fullscreenControlPosition) {
      options.fullscreenControlOptions = {
        position: fullscreenControlPosition,
      };
    }

    return options;
  }, [isSatellite]);
  if (!isLoaded) {
    return <div>Loading map...</div>;
  }

  return (
    <MapContainer mapSize={mapSize} className={className}>
      {showSearch && (
        <div className="search-container">
          <Autocomplete
            onLoad={(ref) => {
              autocompleteRef.current = ref;
            }}
            onPlaceChanged={onPlaceChanged}
          >
            <Input
              placeholder="Search for a location..."
              prefix={<SearchOutlined />}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              style={{ border: "none", boxShadow: "none" }}
            />
          </Autocomplete>
        </div>
      )}
      <div
        className="map-controls"
        style={{
          right: mapSize === "small" ? "auto" : "10px",
          left: mapSize === "small" ? "10px" : "auto",
        }}
      >
        <Button
          className="satellite-toggle"
          onClick={toggleSatellite}
          size="large"
        >
          {isSatellite ? "Map" : "Satellite"}
        </Button>
      </div>

      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        center={mapCenter}
        zoom={mapZoom}
        onLoad={(map) => {
          setMap(map);
          mapRef.current = map;
        }}
        onClick={(event) => {
          if (fenceStyle === "2") {
            onMapClick(event);
          } else if (isEditing && customDrawingActive && fenceStyle === "1") {
            handlePolygonPointAdd(event);
          }
        }}
        onDblClick={(event) => {
          if (isEditing && customDrawingActive && fenceStyle === "1") {
            handlePolygonDblClick(event);
          }
        }}
        options={mapOptions}
        onRightClick={
          fenceStyle === "2" && isEditing
            ? (e) => {
              const lat = e.latLng.lat();
              const lng = e.latLng.lng();
              drawCircleAtLocation({ lat, lng }, radius);
            }
            : undefined
        }
      >
        {/* Custom polygon drawing — in-progress visualization */}
        {isEditing && drawingMode === "polygon" && fenceStyle === "1" && customDrawingActive && (
          <>
            {inProgressPoints.length > 1 && (
              <Polyline
                path={inProgressPoints}
                options={{
                  strokeColor: "#06CE06",
                  strokeOpacity: 0.8,
                  strokeWeight: 2,
                  clickable: false,
                }}
              />
            )}
            {inProgressPoints.map((point, index) => (
              <Marker
                key={`draw-point-${index}`}
                position={point}
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: index === 0 ? 7 : 5,
                  fillColor: "#06CE06",
                  fillOpacity: 1,
                  strokeColor: "white",
                  strokeWeight: 1.5,
                }}
                onClick={() => {
                  if (index === 0 && inProgressPoints.length >= 3) {
                    if (clickTimerRef.current) {
                      clearTimeout(clickTimerRef.current);
                      clickTimerRef.current = null;
                    }
                    completePolygon();
                  }
                }}
              />
            ))}
          </>
        )}

        {/* Initial polygon (for edit prefill) */}
        {fenceStyle === "1" &&
          Array.isArray(initialPolygon) &&
          initialPolygon.length > 2 && (
            <Polygon
              paths={initialPolygon.map((p) => ({
                lat: Number(p.lat),
                lng: Number(p.lng),
              }))}
              options={{
                fillColor: "#06CE06",
                fillOpacity: 0.3,
                strokeColor: "#06CE06",
                strokeOpacity: 1.0,
                strokeWeight: 2,
                editable: !!isEditing,
                draggable: false,
              }}
              onLoad={(poly) => {
                // store ref and wire edit listeners when in edit mode
                drawnPolygonRef.current = poly;
                if (isEditing) {
                  wirePolygonListeners(poly);
                  const pts = extractPath(poly);
                  onPathChange && onPathChange(pts);
                }
              }}
            />
          )}

        {/* Initial circle (for edit prefill) */}
        {fenceStyle === "2" && circleCenter && (
          <>
            <Circle
              center={circleCenter}
              radius={radius * 1000}
              options={{
                fillColor: "#06CE06",
                fillOpacity: 0.3,
                strokeColor: "#06CE06",
                strokeOpacity: 1.0,
                strokeWeight: 2,
                editable: !!isEditing,
                draggable: false,
              }}
              onLoad={(circle) => {
                drawnCircleRef.current = circle;
              }}
              onCenterChanged={() => {
                if (!drawnCircleRef.current) return;
                const c = drawnCircleRef.current.getCenter();
                if (!c) return;
                const center = { lat: c.lat(), lng: c.lng() };
                // Avoid loops
                if (
                  lastCircleCenterRef.current &&
                  lastCircleCenterRef.current.lat === center.lat &&
                  lastCircleCenterRef.current.lng === center.lng
                ) {
                  return;
                }
                lastCircleCenterRef.current = center;
                setCircleCenter(center);
                onCircleDraw &&
                  onCircleDraw({
                    center,
                    radius,
                    area: Math.PI * radius * radius,
                  });
              }}
              onRadiusChanged={() => {
                if (!drawnCircleRef.current) return;
                const c = drawnCircleRef.current.getCenter();
                const r = drawnCircleRef.current.getRadius() / 1000;
                // Avoid loops
                if (lastCircleRadiusRef.current === r) return;
                lastCircleRadiusRef.current = r;
                const center = c
                  ? { lat: c.lat(), lng: c.lng() }
                  : circleCenter;
                setCircleCenter(center);
                onCircleDraw &&
                  onCircleDraw({ center, radius: r, area: Math.PI * r * r });
              }}
            />
            <Marker
              position={circleCenter}
              draggable={!!isEditing}
              onDragEnd={(e) => {
                const center = { lat: e.latLng.lat(), lng: e.latLng.lng() };
                setCircleCenter(center);
                if (drawnCircleRef.current) {
                  drawnCircleRef.current.setCenter(center);
                }
                onCircleDraw &&
                  onCircleDraw({
                    center,
                    radius,
                    area: Math.PI * radius * radius,
                  });
              }}
            />
          </>
        )}
        {/* Render markers */}
        {markers.map((marker) => {
          let iconSvg = "";
          let fillColor = "#06CE06";

          switch (marker.type) {
            case "fence":
              fillColor = "#06CE06";
              iconSvg = `
                 <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                   <circle cx="8" cy="8" r="6" fill="${fillColor}" stroke="white" stroke-width="2"/>
                 </svg>
               `;
              break;
            case "start":
              fillColor = "#06CE06";
              iconSvg = `
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="${fillColor}"/>
                </svg>
              `;
              break;
            case "end":
              fillColor = "#06CE06";
              iconSvg = `
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="${fillColor}"/>
                </svg>
              `;
              break;
            default:
              fillColor = "#06CE06";
              iconSvg = `
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="${fillColor}"/>
                </svg>
              `;
          }

          return (
            <React.Fragment key={marker.id}>
              <Marker
                key={marker.id}
                position={marker.position}
                title={marker.title}
                onClick={() => onMarkerClick(marker)}
                icon={{
                  url:
                    "data:image/svg+xml;charset=UTF-8," +
                    encodeURIComponent(iconSvg),
                  scaledSize: new window.google.maps.Size(16, 16),
                  anchor: new window.google.maps.Point(8, 8),
                }}
              />
              {/* Always show lightweight label using OverlayView */}
              <OverlayView
                position={marker.position}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <div
                  style={{
                    background: "#ffffff",
                    color: "#000000",
                    padding: "2px 6px",
                    borderRadius: "4px",
                    fontSize: "14px",
                    fontWeight: 500,
                    border: "1px solid #e0e0e0",
                    minWidth: "100px",
                    maxWidth: "150px",
                    wordBreak: "break-word",
                  }}
                >
                  {marker.title}
                </div>
              </OverlayView>
            </React.Fragment>
          );
        })}

        {/* Render fence polygons */}
        {fencePolygons.map((fencePolygon) => (
          <Polygon
            key={fencePolygon.id}
            paths={fencePolygon.path}
            options={{
              fillColor: "#06CE06",
              fillOpacity: 0.3,
              strokeColor: "#06CE06",
              strokeOpacity: 1.0,
              strokeWeight: 2,
            }}
          />
        ))}

        {/* Render polygons directly from routeData.FRoutePassInfo in edit mode */}
        {mode === "edit" &&
          Array.isArray(routeData?.FRoutePassInfo) &&
          routeData.FRoutePassInfo.map((rf) => {
            let path = [];
            try {
              path = JSON.parse(rf.FPassFencePath || "[]");
            } catch {
              path = [];
            }
            const valid = Array.isArray(path)
              ? path.filter(
                (p) =>
                  typeof p.lat === "number" &&
                  typeof p.lng === "number" &&
                  !isNaN(p.lat) &&
                  !isNaN(p.lng),
              )
              : [];
            if (valid.length < 3) return null;
            const polygonPath = valid.map((p) => ({
              lat: parseFloat(p.lat),
              lng: parseFloat(p.lng),
            }));
            return (
              <Polygon
                key={`rf-${rf.FPassFenceGUID || rf.FPassFenceName}`}
                paths={polygonPath}
                options={{
                  fillColor: "#06CE06",
                  fillOpacity: 0.3,
                  strokeColor: "#06CE06",
                  strokeOpacity: 1.0,
                  strokeWeight: 2,
                }}
              />
            );
          })}

        {/* Center markers for FRoutePassInfo */}
        {mode === "edit" &&
          Array.isArray(routeData?.FRoutePassInfo) &&
          routeData.FRoutePassInfo.map((rf) => {
            const lat = parseFloat(rf.FPassFenceCenterLat);
            const lng = parseFloat(rf.FPassFenceCenterLon);
            if (isNaN(lat) || isNaN(lng)) return null;
            const iconSvg = `
             <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
               <circle cx="8" cy="8" r="6" fill="#06CE06" stroke="white" stroke-width="2"/>
             </svg>
           `;
            return (
              <Marker
                key={`rfm-${rf.FPassFenceGUID || rf.FPassFenceName}`}
                position={{ lat, lng }}
                title={rf.FPassFenceName || "Fence"}
                icon={{
                  url:
                    "data:image/svg+xml;charset=UTF-8," +
                    encodeURIComponent(iconSvg),
                  scaledSize: new window.google.maps.Size(16, 16),
                  anchor: new window.google.maps.Point(8, 8),
                }}
              />
            );
          })}
        {mode === "edit" &&
          Array.isArray(routeData?.FRoutePassInfo) &&
          routeData.FRoutePassInfo.map((rf) => {
            const lat = parseFloat(rf.FPassFenceCenterLat);
            const lng = parseFloat(rf.FPassFenceCenterLon);
            if (isNaN(lat) || isNaN(lng)) return null;
            return (
              <OverlayView
                key={`rfw-${rf.FPassFenceGUID || rf.FPassFenceName}`}
                position={{ lat, lng }}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <div
                  style={{
                    background: "#ffffff",
                    color: "#000000",
                    padding: "2px 6px",
                    borderRadius: "4px",
                    fontSize: "12px",
                    fontWeight: 600,
                    border: "1px solid #e0e0e0",
                    whiteSpace: "nowrap",
                  }}
                >
                  {rf.FPassFenceName || "Fence"}
                </div>
              </OverlayView>
            );
          })}

        {/* Render route polyline: prefer explicit FRoadPoints, else fall back to rGeoJSON */}
        {Array.isArray(routePoints) && routePoints.length > 1 ? (
          <Polyline
            path={routePoints}
            options={{
              strokeColor: "#06CE06",
              strokeOpacity: 1.0,
              strokeWeight: 6,
            }}
          />
        ) : (
          rGeoJSON &&
          rGeoJSON.type === "LineString" &&
          rGeoJSON.coordinates && (
            <Polyline
              path={rGeoJSON.coordinates.map((coord) => ({
                lat: coord[1],
                lng: coord[0],
              }))}
              options={{
                strokeColor: "#06CE06",
                strokeOpacity: 1.0,
                strokeWeight: 6,
              }}
            />
          )
        )}

        {/* Render directions if available */}
        {directions && (
          <DirectionsRenderer
            directions={directions}
            options={{
              suppressMarkers: true,
              polylineOptions: {
                strokeColor: "#06CE06",
                strokeWeight: 4,
              },
            }}
          />
        )}

        {/* Marker for search location */}
        {searchLocation && (
          <Marker
            position={searchLocation}
            icon={{
              url: "https://maps.google.com/mapfiles/ms/icons/red-dot.png", // default red marker
            }}
          />
        )}

        {!isEditing &&
          Array.isArray(fenceData) &&
          fenceData.length > 0 &&
          fenceData.map((fence) => {
            if (!fence.FPath) return null;
            const path = JSON.parse(fence.FPath);
            if (!Array.isArray(path) || path.length < 3) return null;

            return (
              <Polygon
                key={fence.FGUID}
                paths={path.map((p) => ({
                  lat: Number(p.lat),
                  lng: Number(p.lng),
                }))}
                options={{
                  fillColor: "#06CE06",
                  fillOpacity: 0.3,
                  strokeColor: "#06CE06",
                  strokeOpacity: 1.0,
                  strokeWeight: 2,
                }}
              />
            );
          })}
      </GoogleMap>
    </MapContainer>
  );
};

export default GoogleMapComponent;
